import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';

// Global refresh function to notify all useTags instances to refresh
let globalRefreshCallbacks = [];

export const useTags = ({ itemId } = {}) => {
  const [tags, setTags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchTags = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/user/tags`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch tags');
      }

      const data = await response.json();
      setTags(data);
    } catch (error) {
      console.error('Error fetching tags:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchItemTags = async () => {
    const token = localStorage.getItem('token');
    if (!token || !itemId) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/receipt-items/tags?item_id=${itemId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch item tags');
      }

      const data = await response.json();
      setTags(data);
    } catch (error) {
      console.error('Error fetching item tags:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshTags = itemId ? fetchItemTags : fetchTags;

  // Register this instance for global refreshes
  useEffect(() => {
    const refreshCallback = () => {
      // Only refresh tag selection modals (those without itemId)
      // Item-specific tag lists don't need to refresh since they show applied tags
      if (!itemId) {
        fetchTags();
      }
    };

    globalRefreshCallbacks.push(refreshCallback);

    return () => {
      globalRefreshCallbacks = globalRefreshCallbacks.filter(cb => cb !== refreshCallback);
    };
  }, [itemId]);

  useEffect(() => {
    if (itemId) {
      fetchItemTags();
    } else {
      fetchTags();
    }
  }, [itemId]);

  const createTag = async (name, description = '') => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/user/tag/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          description,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create tag');
      }

      const newTag = await response.json();
      
      // Refresh all tag selection modals to get the latest tags from server
      globalRefreshCallbacks.forEach(callback => callback());
      
      return newTag;
    } catch (error) {
      console.error('Error creating tag:', error);
      throw error;
    }
  };

  const addTag = async (tagId) => {
    if (!itemId) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/receipt-items/tag`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          item_id: itemId,
          tag_id: tagId,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to add tag');
      }

      // Refresh tags after adding
      await fetchItemTags();
      
      return true;
    } catch (error) {
      console.error('Error adding tag:', error);
      throw error;
    }
  };

  const removeTag = async (tagId) => {
    if (!itemId) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/receipt-items/untag?item_id=${itemId}&tag_id=${tagId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to remove tag');
      }

      // Refresh tags after removing
      await fetchItemTags();
    } catch (error) {
      console.error('Error removing tag:', error);
      throw error;
    }
  };

  return {
    tags,
    isLoading,
    addTag,
    removeTag,
    createTag,
    refreshTags: itemId ? fetchItemTags : fetchTags,
  };
};